import { useMemo } from 'react';

import { freeze } from '@trello/objects';
import type { TrelloQueryHookOptions } from '@trello/quickload';
import { getLocation } from '@trello/router';
import { getRouteIdFromPathname, isBoardRoute } from '@trello/routes';

type Options = Pick<
  TrelloQueryHookOptions<object, object>,
  'fetchPolicy' | 'waitOn'
>;

const BOARD_ROUTE_OPTIONS = freeze<Options>({
  fetchPolicy: 'cache-only',
  waitOn: ['CurrentBoardFull'],
});

const NON_BOARD_ROUTE_OPTIONS = freeze<Options>({
  waitOn: ['None'],
});

/**
 * The CardFront component can be rendered in a variety of different places.
 * When it's rendered in a board view, we can generally rely on the cache to
 * hydrate all its queries. On non-board views, however, we may need to fetch.
 * This is a lightweight abstraction designed to make our query hook options
 * consistent across all CardFront query hooks.
 */
export const useCardFrontQueryHookOptions = (): Readonly<Options> => {
  // NOTE: This is intentionally non-reactive!
  return useMemo(() => {
    const { pathname } = getLocation();
    const routeId = getRouteIdFromPathname(pathname);
    return isBoardRoute(routeId)
      ? BOARD_ROUTE_OPTIONS
      : NON_BOARD_ROUTE_OPTIONS;
  }, []);
};
